import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const buyingSignalsArticle: BlogArticle = {
  key: BlogArticleKey.BUYING_SIGNALS,
  slug: BlogSlugs.BUYING_SIGNALS,
  categoryId: "04",
  date: "2026-08-30",
  readTime: 7,
  locales: {
    fr: {
      title: "Contacter au bon moment : les signaux qui valent quelque chose",
      excerpt:
        "Le bon prospect contacté six mois trop tôt est une affaire perdue. Ce qui sépare les deux n'est pas le profil, c'est un événement, et il est visible.",
      description:
        "Quels signaux indiquent qu'un prospect est prêt maintenant, où les voir sans outil de veille, et comment les transformer en une raison d'écrire aujourd'hui.",
      ctaTitle: "Repère le signal, pas seulement le profil",
      ctaText:
        "On tire de tes appels ce qui a déclenché l'achat chez tes clients, puis on cherche ce même déclencheur dans ton réseau. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Deux personnes ont exactement le même profil. L'une répond dans l'heure, l'autre ne répondra jamais. Ce n'est pas le message qui fait la différence, c'est que l'une vient de changer quelque chose et l'autre non.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "C'est la dimension que le ciblage oublie presque toujours. On travaille beaucoup le *qui*, un peu le *quoi dire*, et jamais le *quand*, alors que c'est lui qui décide de la moitié des réponses.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Un bon prospect contacté six mois trop tôt n'est pas un bon prospect, c'est une affaire que tu viens de brûler.",
        },
        { type: ArticleBlockType.H2, text: "Ce qu'est un signal, et ce qui n'en est pas un" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un signal est un événement daté qui change la situation de la personne, et qui a une conséquence sur ce que tu vends. Trois conditions, et il faut les trois.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "*Directeur commercial d'une PME* n'en est pas un : c'est un état, il dure des années, il ne dit rien de maintenant. *Vient de prendre la direction commerciale d'une PME* en est un, parce qu'il est daté, parce qu'il change tout pour la personne, et parce que les premiers mois d'un poste sont précisément le moment où l'on change les outils.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ce qui n'est pas un signal", "Le signal correspondant"],
          rows: [
            ["Entreprise de cinquante personnes", "A recruté quinze personnes en six mois"],
            ["A une équipe commerciale", "Recrute son premier commercial"],
            ["Utilise un CRM", "En change, ou vient d'en installer un"],
            ["Fait de la prospection", "Publie une offre d'emploi de SDR"],
            ["Dirigeant depuis dix ans", "Vient de lever, ou vient d'ouvrir un second bureau"],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La colonne de gauche décrit des gens. La colonne de droite décrit des moments. On prospecte des moments.",
        },
        { type: ArticleBlockType.H2, text: "Où les voir sans payer un outil de veille" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La plupart des signaux utiles sont publics, et personne ne les regarde parce qu'ils sont dispersés. Aucun ne demande d'abonnement.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Le parcours d'un profil.** La date de prise de poste est affichée. Entre le premier et le sixième mois, quelqu'un a le mandat de changer les choses, et l'envie.",
            "**Les offres d'emploi.** Une entreprise qui recrute un poste dit ce qu'elle n'arrive pas à faire elle-même. C'est le signal le plus honnête qui existe.",
            "**Ce que la personne publie ou commente.** Quelqu'un qui écrit sur un sujet y pense depuis des semaines.",
            "**Les changements d'équipe autour d'elle.** Un départ, une arrivée, une réorganisation redistribuent les priorités.",
            "**Ce qu'elle a déjà essayé.** Un outil affiché sur un profil, une certification récente, un partenaire annoncé.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le deuxième mérite d'être pris au sérieux. Une offre d'emploi est une déclaration publique et détaillée d'un manque, écrite par l'entreprise elle-même, avec le budget en face. Peu de signaux sont aussi explicites.",
        },
        { type: ArticleBlockType.H2, text: "Trouver les tiens" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les signaux ci-dessus sont génériques. Les tiens sont ailleurs, et ils sont dans tes appels : à un moment, chacun de tes clients a dit pourquoi il s'est mis à chercher maintenant plutôt que l'an dernier.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "C'est la question la plus rentable d'un appel de découverte, et l'une des moins posées : *qu'est-ce qui a fait que vous vous en occupez maintenant ?* La réponse est ton déclencheur, et si elle revient chez plusieurs clients, tu tiens un critère de ciblage qui vaut tous les filtres.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Prospecter sur le profil",
            items: [
              "La liste ne change pas d'un mois sur l'autre",
              "Aucune urgence à contacter aujourd'hui",
              "Le message doit créer le besoin",
              "Un refus ferme la porte pour longtemps",
            ],
          },
          good: {
            title: "Prospecter sur le signal",
            items: [
              "La liste se renouvelle toute seule",
              "Une raison d'écrire cette semaine précisément",
              "Le message part d'un fait déjà reconnu",
              "Un refus dit surtout que le signal était mal lu",
            ],
          },
        },
        { type: ArticleBlockType.H2, text: "Le signal est aussi ta première phrase" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Il y a un bénéfice secondaire qu'on remarque tard. Le signal qui t'a fait retenir un profil est exactement ce qui rend ton premier message légitime.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Sans lui, tu ouvres en te présentant, et rien ne justifie que tu écrives aujourd'hui. Avec lui, la première phrase existe déjà, et elle ne fonctionne que pour cette personne, ce qui est la meilleure garantie qu'elle ne ressemble pas à un envoi de masse.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une réserve, parce qu'elle est vite franchie : mentionne le fait, jamais l'interprétation. *Tu as pris ce poste en mars* est un fait. *Tu dois avoir du mal à structurer l'équipe* est une supposition, et elle se retourne contre toi si elle est fausse.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "On repère dans tes appels ce qui a déclenché la recherche chez ceux qui ont acheté, et ces déclencheurs deviennent des critères. L'extension lit ensuite les profils de ton réseau, remonte ceux où le signal est présent, et te donne pour chacun le fait qui l'a fait retenir.",
          linkLabel: "Découvrir la recherche de prospects",
          linkPath: ProtectedPaths.PROSPECT_SEARCH,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Arrête de chercher des gens, cherche des moments. Un signal est daté, il change la situation, et il a une conséquence sur ce que tu vends. Les tiens sont dans tes appels, dans la réponse à une seule question : qu'est-ce qui a fait que c'est maintenant.",
        },
      ],
      faq: [
        {
          question: "Qu'est-ce qu'un signal d'achat en B2B ?",
          answer:
            "Un événement daté qui change la situation d'une personne et qui a une conséquence sur ce que tu vends. Les trois conditions comptent : un état permanent comme un intitulé de poste n'est pas un signal, une prise de poste récente en est un.",
        },
        {
          question: "Où trouver des signaux d'achat sans outil payant ?",
          answer:
            "Dans ce qui est déjà public : la date de prise de poste sur un profil, les offres d'emploi de l'entreprise, ce que la personne publie ou commente, les mouvements d'équipe autour d'elle. L'offre d'emploi est le signal le plus explicite, puisqu'une entreprise y déclare elle-même ce qu'elle n'arrive pas à faire.",
        },
        {
          question: "Comment savoir quels signaux comptent pour mon offre ?",
          answer:
            "En demandant à tes clients ce qui a fait qu'ils s'en sont occupés à ce moment-là plutôt qu'un an plus tôt. Si la même réponse revient chez plusieurs, tu tiens ton déclencheur, et il vaut mieux que n'importe quel filtre.",
        },
        {
          question: "Faut-il mentionner le signal dans son message ?",
          answer:
            "Oui, mais le fait, pas ton interprétation. « Tu as pris ce poste en mars » se vérifie. « Tu dois avoir du mal à structurer ton équipe » est une supposition, et elle se retourne contre toi dès qu'elle est fausse.",
        },
      ],
    },
    en: {
      title: "Reaching out at the right time: the signals that are worth something",
      excerpt:
        "The right prospect contacted six months too early is a lost deal. What separates the two is not the profile, it is an event, and it is visible.",
      description:
        "Which signals show a prospect is ready now, where to see them without a monitoring tool, and how to turn one into a reason to write today.",
      ctaTitle: "Spot the signal, not just the profile",
      ctaText:
        "We pull from your calls what triggered the purchase for your customers, then look for that same trigger across your network. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Two people have exactly the same profile. One replies within the hour, the other never will. It is not the message that separates them, it is that one of them just changed something and the other did not.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "This is the dimension targeting almost always forgets. We work hard on the *who*, a little on the *what to say*, and never on the *when*, even though it decides half the replies.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A good prospect contacted six months too early is not a good prospect, it is a deal you just burned.",
        },
        { type: ArticleBlockType.H2, text: "What a signal is, and what is not one" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A signal is a dated event that changes the person's situation and has a consequence for what you sell. Three conditions, and you need all three.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "*Sales director at a mid-size company* is not one: it is a state, it lasts years, it says nothing about now. *Just took over sales at a mid-size company* is one, because it is dated, because it changes everything for that person, and because the first months in a role are exactly when tools get replaced.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["What is not a signal", "The matching signal"],
          rows: [
            ["A fifty-person company", "Hired fifteen people in six months"],
            ["Has a sales team", "Is hiring its first salesperson"],
            ["Uses a CRM", "Is switching, or just installed one"],
            ["Does outbound", "Posted an SDR job opening"],
            ["Founder for ten years", "Just raised, or just opened a second office"],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The left column describes people. The right column describes moments. You prospect moments.",
        },
        { type: ArticleBlockType.H2, text: "Where to see them without paying for monitoring" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Most useful signals are public, and nobody looks at them because they are scattered. None of them requires a subscription.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**A profile's career history.** The start date is displayed. Between month one and month six, someone has both the mandate and the appetite to change things.",
            "**Job postings.** A company hiring for a role is stating what it cannot do itself. It is the most honest signal there is.",
            "**What the person publishes or comments on.** Someone writing about a subject has been thinking about it for weeks.",
            "**Team changes around them.** A departure, an arrival, a reorganisation redistribute priorities.",
            "**What they already tried.** A tool listed on a profile, a recent certification, an announced partner.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The second deserves to be taken seriously. A job posting is a public, detailed declaration of a gap, written by the company itself, with budget attached. Few signals are that explicit.",
        },
        { type: ArticleBlockType.H2, text: "Finding your own" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The signals above are generic. Yours are elsewhere, and they are in your calls: at some point, every one of your customers said why they started looking now rather than last year.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "It is the highest-return question in a discovery call and one of the least asked: *what made you deal with this now?* The answer is your trigger, and if it recurs across several customers, you hold a targeting criterion worth more than any filter.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Prospecting on the profile",
            items: [
              "The list does not change from month to month",
              "No urgency to write today",
              "The message has to create the need",
              "A refusal closes the door for a long time",
            ],
          },
          good: {
            title: "Prospecting on the signal",
            items: [
              "The list renews itself",
              "A reason to write this specific week",
              "The message starts from an already acknowledged fact",
              "A refusal mostly says the signal was misread",
            ],
          },
        },
        { type: ArticleBlockType.H2, text: "The signal is also your opening line" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "There is a secondary benefit people notice late. The signal that made you keep a profile is exactly what makes your first message legitimate.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Without it you open by introducing yourself, and nothing justifies writing today. With it the first sentence already exists, and it only works for that one person, which is the best guarantee it does not read as a mass send.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "One caution, because it is crossed easily: mention the fact, never the interpretation. *You started this role in March* is a fact. *You must be struggling to structure the team* is a guess, and it turns against you the moment it is wrong.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "We surface from your calls what triggered the search for the people who bought, and those triggers become criteria. The extension then reads the profiles in your network, brings up the ones where the signal is present, and gives you for each the fact that kept it.",
          linkLabel: "See how prospect search works",
          linkPath: ProtectedPaths.PROSPECT_SEARCH,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Stop looking for people, look for moments. A signal is dated, it changes the situation, and it has a consequence for what you sell. Yours are in your calls, in the answer to a single question: what made it now.",
        },
      ],
      faq: [
        {
          question: "What is a B2B buying signal?",
          answer:
            "A dated event that changes a person's situation and has a consequence for what you sell. All three conditions matter: a permanent state such as a job title is not a signal, a recent move into a role is.",
        },
        {
          question: "Where do you find buying signals without a paid tool?",
          answer:
            "In what is already public: the start date on a profile, the company's job postings, what the person publishes or comments on, team movements around them. The job posting is the most explicit, since a company states there what it cannot do itself.",
        },
        {
          question: "How do you know which signals matter for your offer?",
          answer:
            "By asking your customers what made them deal with it then rather than a year earlier. If the same answer recurs across several, you hold your trigger, and it is worth more than any filter.",
        },
        {
          question: "Should you mention the signal in your message?",
          answer:
            "Yes, but the fact, not your interpretation. You started this role in March can be verified. You must be struggling to structure your team is a guess, and it turns against you as soon as it is wrong.",
        },
      ],
    },
  },
};
